import React from 'react';
import { Processor } from '../../types/dashboard';
import { MoreHorizontal, TrendingUp, AlertCircle } from 'lucide-react';
interface ProcessorHeatmapProps {
  processors: Processor[];
  onProcessorClick?: (processor: Processor) => void;
}
export function ProcessorHeatmap({
  processors,
  onProcessorClick
}: ProcessorHeatmapProps) {
  // Helper to determine color based on capacity
  const getCapacityColor = (capacity: number) => {
    if (capacity >= 90) return 'bg-rose-500';
    if (capacity >= 75) return 'bg-amber-500';
    if (capacity >= 50) return 'bg-emerald-500';
    return 'bg-blue-500';
  };
  const getCapacityBg = (capacity: number) => {
    if (capacity >= 90) return 'bg-rose-50 border-rose-200';
    if (capacity >= 75) return 'bg-amber-50 border-amber-200';
    if (capacity >= 50) return 'bg-emerald-50 border-emerald-200';
    return 'bg-blue-50 border-blue-200';
  };
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-213">
      <div className="p-5 border-b border-slate-100 flex justify-between items-center" data-id="element-214">
        <h3 className="font-bold text-slate-800 flex items-center gap-2" data-id="element-215">
          <TrendingUp className="w-5 h-5 text-blue-600" data-id="element-216" />
          Processor Capacity Heatmap
        </h3>
        <div className="flex gap-4 text-xs font-medium text-slate-500" data-id="element-217">
          <div className="flex items-center gap-1" data-id="element-218">
            <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-219"></div>Open
          </div>
          <div className="flex items-center gap-1" data-id="element-220">
            <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-221"></div>Optimal
          </div>
          <div className="flex items-center gap-1" data-id="element-222">
            <div className="w-2 h-2 rounded-full bg-amber-500" data-id="element-223"></div>Heavy
          </div>
          <div className="flex items-center gap-1" data-id="element-224">
            <div className="w-2 h-2 rounded-full bg-rose-500" data-id="element-225"></div>Critical
          </div>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-226">
        {processors.map(processor => <div key={processor.id} onClick={() => onProcessorClick?.(processor)} className={`relative p-4 rounded-lg border transition-all hover:shadow-md cursor-pointer group ${getCapacityBg(processor.capacity)}`} data-id="element-227">
            <div className="flex justify-between items-start mb-3" data-id="element-228">
              <div className="flex items-center gap-3" data-id="element-229">
                <div className="relative" data-id="element-230">
                  <img src={processor.avatar} alt={processor.name} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" data-id="element-231" />
                  <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${processor.status === 'active' ? 'bg-emerald-500' : processor.status === 'away' ? 'bg-amber-500' : 'bg-slate-400'}`} data-id="element-232" />
                </div>
                <div data-id="element-233">
                  <h4 className="font-bold text-slate-900" data-id="element-234">{processor.name}</h4>
                  <p className="text-xs text-slate-600" data-id="element-235">
                    {processor.specialty} Specialist
                  </p>
                </div>
              </div>
              <button className="text-slate-400 hover:text-slate-700" data-id="element-236">
                <MoreHorizontal className="w-5 h-5" data-id="element-237" />
              </button>
            </div>

            <div className="space-y-3" data-id="element-238">
              <div data-id="element-239">
                <div className="flex justify-between text-xs mb-1" data-id="element-240">
                  <span className="font-medium text-slate-700" data-id="element-241">
                    Capacity Load
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-242">
                    {processor.capacity}%
                  </span>
                </div>
                <div className="w-full h-2 bg-white/50 rounded-full overflow-hidden" data-id="element-243">
                  <div className={`h-full rounded-full transition-all duration-500 ${getCapacityColor(processor.capacity)}`} style={{
                width: `${processor.capacity}%`
              }} data-id="element-244" />
                </div>
              </div>

              <div className="flex justify-between items-center pt-2 border-t border-black/5" data-id="element-245">
                <div className="text-center" data-id="element-246">
                  <p className="text-xs text-slate-500" data-id="element-247">Active Loans</p>
                  <p className="font-bold text-slate-900" data-id="element-248">
                    {processor.activeLoans}
                  </p>
                </div>
                <div className="text-center" data-id="element-249">
                  <p className="text-xs text-slate-500" data-id="element-250">Efficiency</p>
                  <p className="font-bold text-slate-900" data-id="element-251">
                    {processor.efficiency}%
                  </p>
                </div>
                <div className="text-center" data-id="element-252">
                  <p className="text-xs text-slate-500" data-id="element-253">Status</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${processor.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'}`} data-id="element-254">
                    {processor.status}
                  </span>
                </div>
              </div>
            </div>

            {processor.capacity > 90 && <div className="absolute -top-2 -right-2 bg-rose-500 text-white p-1 rounded-full shadow-lg animate-pulse" data-id="element-255">
                <AlertCircle className="w-4 h-4" data-id="element-256" />
              </div>}
          </div>)}
      </div>
    </div>;
}